import React from 'react';
import transferCallChangelog from './transfer-call.changes.md';

import { HandleType, NodeToolbar, Position } from 'reactflow';
import { useSelector } from 'react-redux';
import { selectCommandByNodeId, State } from '@store/index';
import { Stack } from '@mui/material';
import { NodeWebhookAnchors } from '@common/Node/Defaults/NodeWebhookAnchors';
import { useNodeError } from '@common/hooks/useNodeError';
import { NodeContainer, NodeErrorToolbar, NodeHeader } from '@common/Node';
import { useNodeOutline } from '@common/hooks/useNodeOutline';
import { getAMDAnchors } from '../Dial/Dial';

import {
  NodeConfig,
  NodeField,
  NodeFieldType,
  NodeInputType,
  NodeOutput,
  NodeType,
  TransferCallNodeState,
} from '../..';

export const config: NodeConfig = {
  version: '1.1',
  title: 'Transfer Call',
  identifier: 'TransferCall',
  command_id: 'call.transfer',
  icon: 'telnyx',
  background: '#000000',
  changelog: transferCallChangelog,
  anchors: [
    {
      label: 'Execute',
      id: 'call.transfer.execute',
      type: 'target' as HandleType,
      variant: 'execution',
      isHandle: true,
    },
    {
      label: 'Data',
      id: 'call.transfer.input_data',
      type: 'target' as HandleType,
      variant: 'data',
      isHandle: true,
    },
    {
      // id needs to match the event_type of the webhook
      id: 'call.initiated',
      label: 'call.initiated',
      type: 'source' as HandleType,
      variant: 'webhook_execution',
    },
    {
      label: 'Data',
      id: 'call.initiated.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
    {
      // id needs to match the event_type of the webhook
      id: 'call.bridged',
      label: 'call.bridged',
      type: 'source' as HandleType,
      variant: 'webhook_execution',
    },
    {
      label: 'Data',
      id: 'call.bridged.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
    {
      // id needs to match the event_type of the webhook
      id: 'call.answered',
      label: 'call.answered',
      type: 'source' as HandleType,
      variant: 'webhook_execution',
    },
    {
      label: 'Data',
      id: 'call.answered.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
    {
      // id needs to match the event_type of the webhook
      id: 'call.hangup',
      label: 'call.hangup',
      type: 'source' as HandleType,
      variant: 'webhook_execution',
    },
    {
      label: 'Data',
      id: 'call.hangup.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },

    {
      // id needs to match the event_type of the webhook
      id: 'call.machine.premium.detection.ended',
      label: 'call.machine.premium.detection.ended',
      type: 'source' as HandleType,
      variant: 'webhook_execution',
    },
    {
      label: 'Data',
      id: 'call.machine.premium.detection.ended.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
    {
      // id needs to match the event_type of the webhook
      id: 'call.machine.premium.greeting.ended',
      label: 'call.machine.premium.greeting.ended',
      type: 'source' as HandleType,
      variant: 'webhook_execution',
    },
    {
      label: 'Data',
      id: 'call.machine.premium.greeting.ended.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
    {
      // id needs to match the event_type of the webhook
      id: 'call.machine.detection.ended',
      label: 'call.machine.detection.ended',
      type: 'source' as HandleType,
      variant: 'webhook_execution',
    },
    {
      label: 'Data',
      id: 'call.machine.detection.ended.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
    {
      // id needs to match the event_type of the webhook
      id: 'call.machine.greeting.ended',
      label: 'call.machine.greeting.ended',
      type: 'source' as HandleType,
      variant: 'webhook_execution',
    },
    {
      label: 'Data',
      id: 'call.machine.greeting.ended.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
  ],
  webhooks: [
    {
      webhook: 'call.initiated',
      expected: true,
      multi: false,
      // the suffix is used to determine the webhooks for Call LEG B
      suffix: 'transfer',
    },
    {
      webhook: 'call.bridged',
      expected: true,
      multi: false,
    },
    {
      webhook: 'call.answered',
      expected: true,
      multi: false,
      // the suffix is used to determine the webhooks for Call LEG B
      suffix: 'transfer',
    },
    {
      webhook: 'call.hangup',
      expected: true,
      multi: false,
      // the suffix is used to determine the webhooks for Call LEG B
      suffix: 'transfer',
    },

    {
      webhook: 'call.machine.premium.detection.ended',
      expected: false,
      multi: false,
      suffix: 'transfer',
    },
    {
      webhook: 'call.machine.premium.greeting.ended',
      expected: false,
      multi: false,
      suffix: 'transfer',
    },
    {
      webhook: 'call.machine.detection.ended',
      expected: false,
      multi: false,
      suffix: 'transfer',
    },
    {
      webhook: 'call.machine.greeting.ended',
      expected: false,
      multi: false,
      suffix: 'transfer',
    },
  ],
};

export const state: TransferCallNodeState = {
  call_control_id: { type: NodeInputType.Expression, value: '' },
  to: { type: NodeInputType.Expression, value: '' },
  from: { type: NodeInputType.Expression, value: '' },
  from_display_name: { type: NodeInputType.Expression, value: '' },
  audio_url: { type: NodeInputType.Expression, value: '' },
  media_name: { type: NodeInputType.Expression, value: '' },
  media_encryption: 'disabled',
  timeout_secs: { type: NodeInputType.Number, value: '30' },
  time_limit_secs: { type: NodeInputType.Number, value: '14400' },
  billing_group_id: { type: NodeInputType.Expression, value: '' },
  send_silence_when_idle: { type: NodeInputType.Option, value: 'false' },
  sip_transport_protocol: 'UDP',
  sip_auth_username: { type: NodeInputType.Expression, value: '' },
  sip_auth_password: { type: NodeInputType.Expression, value: '' },
  early_media: 'true',
  client_state: { type: NodeInputType.Expression, value: '' },
  target_leg_client_state: { type: NodeInputType.Expression, value: '' },
  // headers
  custom_headers: { type: NodeInputType.CallHeader, value: [] },
  sip_headers: { type: NodeInputType.CallHeader, value: [] },
  // answering machine detection
  answering_machine_detection: 'disabled',
  total_analysis_time_millis: { type: NodeInputType.Number, value: '3500' },
  after_greeting_silence_millis: { type: NodeInputType.Number, value: '800' },
  between_words_silence_millis: { type: NodeInputType.Number, value: '50' },
  greeting_duration_millis: { type: NodeInputType.Number, value: '3500' },
  initial_silence_millis: { type: NodeInputType.Number, value: '3500' },
  maximum_number_of_words: { type: NodeInputType.Number, value: '5' },
  maximum_word_length_millis: { type: NodeInputType.Number, value: '3500' },
  silence_threshold: { type: NodeInputType.Number, value: '256' },
  greeting_total_analysis_time_millis: { type: NodeInputType.Number, value: '5000' },
  greeting_silence_duration_millis: { type: NodeInputType.Number, value: '1500' },
  // sound modifications
  sound_modifications_enabled: 'false',
  pitch: { type: NodeInputType.Number, value: '1' },
  semitone: { type: NodeInputType.Number, value: '0' },
  octaves: { type: NodeInputType.Number, value: '0' },
  track: 'outbound',
};

export const fields: NodeField[] = [
  {
    id: 'call_control_id',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Call Control ID',
    description: 'Unique identifier and token for controlling the call',
    placeholder: 'A unique identifier for controlling the call',
    required: true,
  },
  {
    id: 'to',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'To',
    description: 'The DID or SIP URI to dial outbound towards.',
    placeholder: 'DID or SIP URI',
    required: true,
  },
  {
    id: 'from',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'From',
    description:
      'The from number to be used as the caller id presented to the destination (to number). The number should be in +E164 format.',
    placeholder: '+E164 formatted calling number',
    required: true,
  },
  {
    id: 'from_display_name',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'From Display Name',
    description:
      'The from_display_name string to be used as the caller id name (SIP From Display Name) presented to the destination (to number). The string should have a maximum of 128 characters, containing only letters, numbers, spaces, and -_~!.+ special characters. If ommited, the display name will be the same as the number in the from field.',
    placeholder: 'Display Name',
    required: false,
  },
  {
    id: 'audio_url',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Audio URL',
    description:
      'The URL of a file to be played back to the callee when the call is answered. The URL can point to either a WAV or MP3 file. media_name and audio_url cannot be used together in one request.',
    placeholder: 'https://example.com',
    required: false,
  },
  {
    id: 'media_name',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Media Name',
    description:
      'The media_name of a file to be played back on the call. The media_name must point to a file previously uploaded to api.telnyx.com/v2/media by the same user/organization. The file must either be a WAV or MP3 file.',
    placeholder: 'my_media_file',
    required: false,
  },
  {
    id: 'early_media',
    tab: '1',
    type: NodeFieldType.Toggle,
    label: 'Early Media',
    description: 'If set to false, early media will not be passed to the originating leg.',
    options: [
      { label: 'Enabled', value: 'true' },
      { label: 'Disabled', value: 'false' },
    ],
    required: false,
  },
  {
    id: 'media_encryption',
    tab: '1',
    type: NodeFieldType.Option,
    label: 'Media Encryption',
    description: 'Defines whether media should be encrypted on the call.',
    options: [
      { label: 'Disabled', value: 'disabled' },
      { label: 'SRTP', value: 'SRTP' },
    ],
    required: false,
  },
  {
    id: 'timeout_secs',
    tab: '1',
    type: NodeFieldType.NumberExpression,
    default_type: NodeInputType.Number,
    label: 'Timeout (Secs)',
    description:
      'The number of seconds that Telnyx will wait for the call to be answered by the destination to which it is being called. If the timeout is reached before an answer is received, the call will hangup and a call.hangup webhook with a hangup_cause of timeout will be sent. Minimum value is 5 seconds. Maximum value is 600 seconds.',
    placeholder: '30',
    required: false,
  },
  {
    id: 'time_limit_secs',
    tab: '1',
    type: NodeFieldType.NumberExpression,
    default_type: NodeInputType.Number,
    label: 'Time Limit (Secs)',
    description:
      'Sets the maximum duration of a Call Control Leg in seconds. If the time limit is reached, the call will hangup and a call.hangup webhook with a hangup_cause of time_limit will be sent. For example, by setting a time limit of 120 seconds, a Call Leg will be automatically terminated two minutes after being answered. The default time limit is 14400 seconds or 4 hours and this is also the maximum allowed call length.',
    placeholder: '14400',
    required: false,
  },
  {
    id: 'billing_group_id',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Billing Group ID',
    description:
      'Use this field to set the Billing Group ID for the call. Must be a valid and existing Billing Group ID.',
    placeholder: 'uuid',
    required: false,
  },
  {
    id: 'send_silence_when_idle',
    tab: '1',
    type: NodeFieldType.OptionExpression,
    default_type: NodeInputType.Option,
    label: 'Send Silence When Idle',
    description: 'Generate silence RTP packets when no transmission available.',
    required: false,
  },
  {
    id: 'sip_transport_protocol',
    tab: '1',
    type: NodeFieldType.Option,
    label: 'SIP Transport Protocol',
    description: 'Defines SIP transport protocol to be used on the call.',
    required: false,
    options: [
      { label: 'UDP', value: 'UDP' },
      { label: 'TCP', value: 'TCP' },
      { label: 'TLS', value: 'TLS' },
    ],
  },
  {
    id: 'sip_auth_username',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'SIP Auth Username',
    description: 'SIP Authentication username used for SIP challenges.',
    placeholder: 'SIP Username',
    required: false,
  },
  {
    id: 'sip_auth_password',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'SIP Auth Password',
    description: 'SIP Authentication password used for SIP challenges.',
    placeholder: 'SIP Password',
    required: false,
  },
  {
    id: 'client_state',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Client State',
    description: 'Use this field to add state to every subsequent webhook. It must be a valid Base-64 encoded string.',
    placeholder: 'Add state to each subsequent webhook.',
    required: false,
  },
  {
    id: 'target_leg_client_state',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Target Leg Client State',
    description:
      'Use this field to add state to every subsequent webhook for the new leg. It must be a valid Base-64 encoded string.',
    placeholder: 'Add state to each subsequent target leg webhook.',
    required: false,
  },
  {
    id: 'custom_headers',
    type: NodeFieldType.CallHeaders,
    label: 'Custom Headers',
    description: 'Custom headers to be added to the SIP INVITE response.',
    placeholder: 'A formatted array of headers.',
    required: false,
  },
  {
    id: 'sip_headers',
    type: NodeFieldType.CallHeaders,
    label: 'SIP Headers',
    description: 'SIP headers to be added to the SIP INVITE response. Currently only User-to-User header is supported.',
    placeholder: 'A formatted array of headers.',
    required: false,
  },
  {
    id: 'answering_machine_detection',
    type: NodeFieldType.Option,
    label: 'Answering Machine Detection',
    description:
      'Enables Answering Machine Detection. Telnyx offers Premium and Standard detections. With Premium detection, when a call is answered, Telnyx runs real-time detection and sends a call.machine.premium.detection.ended webhook with one of the following results: human_residence, human_business, machine, silence or fax_detected. If we detect a beep, we also send a call.machine.premium.greeting.ended webhook with the result of beep_detected. If we detect a beep before call.machine.premium.detection.ended we only send call.machine.premium.greeting.ended, and if we detect a beep after call.machine.premium.detection.ended, we send both webhooks. With Standard detection, when a call is answered, Telnyx runs real-time detection to determine if it was picked up by a human or a machine and sends an call.machine.detection.ended webhook with the analysis result. If greeting_end or detect_words is used and a machine is detected, you will receive another call.machine.greeting.ended webhook when the answering machine greeting ends with a beep or silence. If detect_beep is used, you will only receive call.machine.greeting.ended if a beep is detected.',
    required: false,
    options: [
      {
        label: 'Disabled',
        value: 'disabled',
      },
      {
        label: 'Detect',
        value: 'detect',
      },
      {
        label: 'Detect Beep',
        value: 'detect_beep',
      },
      {
        label: 'Detect Words',
        value: 'detect_words',
      },
      {
        label: 'Greeting End',
        value: 'greeting_end',
      },
      {
        label: 'Premium',
        value: 'premium',
      },
    ],
  },
  {
    id: 'total_analysis_time_millis',
    tab: 'answering_machine_detection',
    type: NodeFieldType.NumberExpression,
    default_type: NodeInputType.Number,
    label: 'Total Analysis Time (Millis)',
    description: 'Maximum timeout threshold for overall detection.',
    placeholder: '3500',
    required: false,
  },
  {
    id: 'after_greeting_silence_millis',
    tab: 'answering_machine_detection',
    type: NodeFieldType.NumberExpression,
    default_type: NodeInputType.Number,
    label: 'After Greeting Silence (Millis)',
    description: 'Silence duration threshold after a greeting message or voice for it be considered human.',
    placeholder: '800',
    required: false,
  },
  {
    id: 'between_words_silence_millis',
    tab: 'answering_machine_detection',
    type: NodeFieldType.NumberExpression,
    default_type: NodeInputType.Number,
    label: 'Between Words Silence (Millis)',
    description: 'Maximum threshold for silence between words.',
    placeholder: '50',
    required: false,
  },
  {
    id: 'greeting_duration_millis',
    tab: 'answering_machine_detection',
    type: NodeFieldType.NumberExpression,
    default_type: NodeInputType.Number,
    label: 'Greeting Duration (Millis)',
    description: 'Maximum threshold of a human greeting. If greeting longer than this value, considered machine.',
    placeholder: '3500',
    required: false,
  },
  {
    id: 'initial_silence_millis',
    tab: 'answering_machine_detection',
    type: NodeFieldType.NumberExpression,
    default_type: NodeInputType.Number,
    label: 'Initial Silence (Millis)',
    description: 'If initial silence duration is greater than this value, consider it a machine.',
    placeholder: '3500',
    required: false,
  },
  {
    id: 'maximum_number_of_words',
    tab: 'answering_machine_detection',
    type: NodeFieldType.NumberExpression,
    default_type: NodeInputType.Number,
    label: 'Maximum Number of Words',
    description: 'If number of detected words is greater than this value, consder it a machine.',
    placeholder: '5',
    required: false,
  },
  {
    id: 'maximum_word_length_millis',
    tab: 'answering_machine_detection',
    type: NodeFieldType.NumberExpression,
    default_type: NodeInputType.Number,
    label: 'Maximum Word Length (Millis)',
    description: 'If a single word lasts longer than this threshold, consider it a machine.',
    placeholder: '3500',
    required: false,
  },
  {
    id: 'silence_threshold',
    tab: 'answering_machine_detection',
    type: NodeFieldType.NumberExpression,
    default_type: NodeInputType.Number,
    label: 'Silence Threshold',
    description: 'Minimum noise threshold for any analysis.',
    placeholder: '256',
    required: false,
  },
  {
    id: 'greeting_total_analysis_time_millis',
    tab: 'answering_machine_detection',
    type: NodeFieldType.NumberExpression,
    default_type: NodeInputType.Number,
    label: 'Greeting Total Analysis Time (Millis)',
    description: 'If machine already detected, maximum timeout threshold to determine the end of the machine greeting.',
    placeholder: '5000',
    required: false,
  },
  {
    id: 'greeting_silence_duration_millis',
    tab: 'answering_machine_detection',
    type: NodeFieldType.NumberExpression,
    default_type: NodeInputType.Number,
    label: 'Greeting Silence Duration (Millis)',
    description:
      'If machine already detected, maximum threshold for silence between words. If exceeded, the greeting is considered ended.',
    placeholder: '1500',
    required: false,
  },
  {
    id: 'sound_modifications_enabled',
    type: NodeFieldType.Toggle,
    label: 'Sound Modifications',
    description: 'Use this field to modify sound effects, for example adjust the pitch.',
    options: [
      { label: 'Enabled', value: 'true' },
      { label: 'Disabled', value: 'false' },
    ],
    required: false,
  },
  {
    id: 'pitch',
    tab: 'sound_modifications',
    type: NodeFieldType.NumberExpression,
    default_type: NodeInputType.Number,
    label: 'Pitch',
    description: 'Set the pitch directly, value should be > 0, default 1 (lower = lower tone).',
    placeholder: 'Set the pitch directly, value should be > 0.',
    required: false,
    minNumber: 0,
  },
  {
    id: 'semitone',
    tab: 'sound_modifications',
    type: NodeFieldType.NumberExpression,
    default_type: NodeInputType.Number,
    label: 'Semitone',
    description: 'Adjust the pitch in semitones, values should be between -14 and 14, default 0.',
    placeholder: 'Adjust the pitch in semitones, values should be between -14 and 14.',
    required: false,
    maxNumber: 14,
    minNumber: -14,
  },
  {
    id: 'octaves',
    tab: 'sound_modifications',
    type: NodeFieldType.NumberExpression,
    default_type: NodeInputType.Number,
    label: 'Octaves',
    description: 'Adjust the pitch in octaves, values should be between -1 and 1, default 0.',
    placeholder: 'Adjust the pitch in octaves, values should be between -1 and 1.',
    required: false,
    maxNumber: 1,
    minNumber: -1,
  },
  {
    id: 'track',
    tab: 'sound_modifications',
    type: NodeFieldType.Option,
    label: 'Track',
    description: 'The track to which the sound modifications will be applied.',
    options: [
      { label: 'Outbound', value: 'outbound' },
      { label: 'Inbound', value: 'inbound' },
    ],
    required: false,
  },
];

export const output: NodeOutput = {
  default: {
    'call.initiated': {
      call: {
        initiated: {
          call_control_id: 'string',
          connection_id: 'string',
          call_leg_id: 'string',
          custom_headers: 'object[]',
          call_session_id: 'string',
          client_state: 'string | null',
          from: 'string',
          to: 'string',
          start_time: 'string',
          direction: 'string',
          state: 'string',
        },
      },
    },
    'call.bridged': {
      call: {
        bridged: {
          call_control_id: 'string',
          call_leg_id: 'string',
          call_session_id: 'string',
          client_state: 'string | null',
          connection_id: 'number',
          from: 'string',
          to: 'string',
          state: 'string',
        },
      },
    },
    'call.answered': {
      call: {
        answered: {
          call_control_id: 'string',
          call_leg_id: 'string',
          call_session_id: 'string',
          client_state: 'string | null',
          connection_id: 'number',
          start_time: 'string',
          from: 'string',
          to: 'string',
          state: 'string',
        },
      },
    },
    'call.hangup': {
      call: {
        hangup: {
          call_control_id: 'string',
          call_leg_id: 'string',
          call_session_id: 'string',
          client_state: 'string | null',
          connection_id: 'number',
          end_time: 'string',
          from: 'string',
          hangup_cause: 'string',
          hangup_source: 'string',
          sip_hangup_cause: 'string',
          start_time: 'string',
          to: 'string',
        },
      },
    },

    'call.machine.premium.detection.ended': {
      call: {
        machine: {
          premium: {
            detection: {
              ended: {
                call_control_id: 'string',
                call_leg_id: 'string',
                call_session_id: 'string',
                client_state: 'string | null',
                connection_id: 'number',
                from: 'string',
                to: 'string',
                result: 'string',
              },
            },
          },
        },
      },
    },
    'call.machine.premium.greeting.ended': {
      call: {
        machine: {
          premium: {
            greeting: {
              ended: {
                call_control_id: 'string',
                call_leg_id: 'string',
                call_session_id: 'string',
                client_state: 'string | null',
                connection_id: 'number',
                from: 'string',
                to: 'string',
                result: 'string',
              },
            },
          },
        },
      },
    },
    'call.machine.detection.ended': {
      call: {
        machine: {
          detection: {
            ended: {
              call_control_id: 'string',
              call_leg_id: 'string',
              call_session_id: 'string',
              client_state: 'string | null',
              connection_id: 'number',
              from: 'string',
              to: 'string',
              result: 'string',
            },
          },
        },
      },
    },
    'call.machine.greeting.ended': {
      call: {
        machine: {
          greeting: {
            ended: {
              call_control_id: 'string',
              call_leg_id: 'string',
              call_session_id: 'string',
              client_state: 'string | null',
              connection_id: 'number',
              from: 'string',
              to: 'string',
              result: 'string',
            },
          },
        },
      },
    },
  },
  dynamic: null,
};

export const TransferCall: React.FC<NodeType> = ({ id, selected, data }) => {
  const { title, background, anchors, icon } = config;

  const command = useSelector((state: State) => selectCommandByNodeId(state, id));

  const { isNodeError, nodeError, nodeStatus } = useNodeError({ command });
  const { nodeOutline } = useNodeOutline({ command, selected });

  if (!command) return null;
  if (command.config.command_id !== 'call.transfer') return null;

  const state = command.state as TransferCallNodeState;
  const isToolbarVisible = Boolean(isNodeError);

  return (
    <NodeContainer selected={selected} nodeOutline={nodeOutline} error={false}>
      <NodeToolbar offset={18} isVisible={isToolbarVisible} position={Position.Top}>
        <Stack direction='row' spacing={1}>
          {isNodeError && <NodeErrorToolbar error={nodeError} />}
        </Stack>
      </NodeToolbar>
      <NodeHeader id={id} title={title} icon={icon} background={background} status={nodeStatus} />
      <NodeWebhookAnchors id={id} command={command} data={data} anchors={getAMDAnchors(state, anchors)} />
    </NodeContainer>
  );
};

export default { config, state, fields, output };
